import { getDirectusArticle, getDirectusArticleList, getDirectusCollection, getDirectusPage } from "./directus";
import { siteContent, supportedLocales } from "../content/site-content.js";

export { supportedLocales };

const SERVICE_ICONS = ["layers", "palette", "smartphone", "radio-tower", "cpu", "cloud"];
const PRODUCT_ICONS = ["satellite", "truck", "key-round", "map-pinned", "handshake", "tag"];

let fallbackIndexPromise;

async function getFallbackIndex() {
  if (!fallbackIndexPromise) {
    fallbackIndexPromise = fetch("/content/local-content.json")
      .then((response) => (response.ok ? response.json() : { pages: [] }))
      .catch(() => ({ pages: [] }));
  }
  return fallbackIndexPromise;
}

export function parsePath(pathname) {
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  const locale = parts[0] === "th" ? "th" : "en";
  const slugParts = locale === "th" ? parts.slice(1) : parts;
  const raw = slugParts.join("/") || "home";
  // Decode percent-encoded paths so Thai/UTF-8 slugs match the fallback index.
  let slug = raw;
  try {
    slug = decodeURIComponent(raw);
  } catch {
    slug = raw;
  }
  return { locale, slug, localizedPrefix: locale === "th" ? "/th" : "" };
}

export async function getFallbackPage(slug, locale) {
  const fallbackIndex = await getFallbackIndex();
  return fallbackIndex.pages.find((page) => page.slug === slug && page.locale === locale) ?? null;
}

export async function getFallbackArticles(locale, limit = 18) {
  const fallbackIndex = await getFallbackIndex();
  return fallbackIndex.pages
    .filter((page) => page.locale === locale && !["home", "articles", "contact-us"].includes(page.slug))
    .slice(0, limit);
}

export async function resolvePage(slug, locale) {
  const directusPage = await getDirectusPage(slug, locale);
  if (directusPage) return { kind: "page", source: "directus", data: normalizeDirectus(directusPage) };

  const directusArticle = await getDirectusArticle(slug, locale);
  if (directusArticle) {
    return { kind: "article", source: "directus", data: normalizeDirectus(directusArticle) };
  }

  const fallback = await getFallbackPage(slug, locale);
  if (fallback) return { kind: "fallback", source: "scraped", data: fallback };

  return null;
}

/* Home/marketing service cards: Directus when present, else static content.
   Mapped to the prototype card shape { icon, title, body, gain }. */
export async function getServices(locale) {
  const items = await getDirectusCollection("services", locale);
  if (!items.length) return siteContent[locale].services;
  return items.map((it, i) => ({
    icon: it.icon || SERVICE_ICONS[i % SERVICE_ICONS.length],
    title: it.title,
    body: it.description || it.excerpt || "",
    gain: it.category || ""
  }));
}

/* Product cards: { icon, title, category, description }. */
export async function getProducts(locale) {
  const items = await getDirectusCollection("products", locale);
  if (!items.length) return siteContent[locale].products;
  return items.map((it, i) => ({
    icon: it.icon || PRODUCT_ICONS[i % PRODUCT_ICONS.length],
    title: it.title,
    category: it.category || "",
    description: it.description || it.excerpt || ""
  }));
}

/* Article cards/reader items: { tag, title, excerpt, date, image, body[] }. */
export async function getArticleItems(locale, { page = 1, limit = 10 } = {}) {
  const result = await getDirectusArticleList(locale, { page, limit });
  const items = result.items || [];
  if (!items.length) {
    const fallbackItems = siteContent[locale].articlesPage.items;
    const start = (page - 1) * limit;
    return {
      items: fallbackItems.slice(start, start + limit),
      total: fallbackItems.length,
      page,
      limit
    };
  }
  return {
    items: items.map((it) => ({
      slug: it.slug,
      route: it.route || `${locale === "th" ? "/th" : ""}/${it.slug}`,
      tag: it.category || "Article",
      title: it.title,
      excerpt: it.description || it.excerpt || "",
      image: normalizeArticleImage(it.hero_image) || firstImageFromBody(it.body),
      date: it.published_at
        ? new Date(it.published_at).toLocaleDateString(locale === "th" ? "th-TH" : "en-US", { month: "short", year: "numeric" })
        : ""
    })),
    total: result.total,
    page,
    limit
  };
}

function normalizeDirectus(item) {
  return {
    slug: item.slug,
    locale: item.locale,
    title: item.hero_title || item.title,
    description: item.hero_text || item.description || item.excerpt || item.seo_description || "",
    body: item.body || "",
    image: normalizeArticleImage(item.hero_image) || firstImageFromBody(item.body),
    seoTitle: item.seo_title || item.title,
    seoDescription: item.seo_description || item.description || item.excerpt || ""
  };
}

function normalizeArticleImage(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.url || value.src || value.id || "";
  }
  return "";
}

function firstImageFromBody(body = "") {
  const htmlMatch = String(body).match(/<img\b[^>]*\bsrc=["']([^"']+)["']/i);
  if (htmlMatch?.[1]) return htmlMatch[1];

  const markdownMatch = String(body).match(/!\[[^\]]*]\(([^)\s]+)(?:\s+"[^"]*")?\)/);
  return markdownMatch?.[1] || "";
}
